import type { EngineService } from './registry.js';

/**
 * The staging region Vinz reserves. It has two roles, so changing it changes both:
 * - it names the reserved region: in every message, in the deploy-job matcher, in discovery's
 *   workspace suffix and in the `VINZ_RESERVED_EXCLUDE` value;
 * - it is the one deploy target of a Serverless/CDK service. Those deploy a region, not a terraform
 *   workspace, so the region itself stands in where an ECS service has its workspace names, and it
 *   keys their observation and `HIST#` rows (a change re-keys those rows, as it does the ECS ones).
 */
export const RESERVED_REGION = 'us-east-2';

/**
 * CI's staging region: it always runs `main`, and a hold never touches it. Only messages and comments name it;
 * the callers' expressions are what send `main` there.
 */
export const CI_REGION = 'us-west-2';

/**
 * The reserved region is everyone's default staging region, so a hold serves the holder's branch to everybody
 * on it, not just the holder. Said wherever a hold is taken or live - the grant reply, the Reserved post and
 * DM, the dashboard's held row - and never on a release or a refusal.
 */
export const RESERVED_IMPACT = `Everyone on ${RESERVED_REGION} staging now hits this branch.`;

/**
 * What a service's rows are keyed by: its ECS workspaces in the reserved region, or the region for a
 * Serverless/CDK service. Nothing for a service with no shared deploy workflow - there is no deploy to read.
 */
export function deployTargets(service: Pick<EngineService, 'path' | 'reservedWorkspaces'>): readonly string[] {
  if (service.reservedWorkspaces.length > 0) return service.reservedWorkspaces;
  return service.path === 'cdk' || service.path === 'serverless' ? [RESERVED_REGION] : [];
}
